import { Helmet } from "react-helmet-async";
import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select } from "../components/ui/select";
import {
  CheckCircle2,
  XCircle,
  Loader2,
  MapPin,
  Phone,
  Mail,
  ArrowRight,
} from "lucide-react";
import StructuredData from "../components/SEO/StructuredData";

const fade = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: "easeOut" },
  }),
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.15 },
  },
};

export default function Contact() {
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email_address: "",
    contact_number: "",
    company_name: "",
    role: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (submitStatus === "error") {
      setSubmitStatus(null);
      setErrorMessage("");
    }
  };

  const sendMail = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    setErrorMessage("");

    const formDataToSend = new FormData();
    Object.keys(formData).forEach((key) => {
      formDataToSend.append(key, formData[key]);
    });

    try {
      const response = await axios({
        method: "post",
        url: "/assets/contact.php",
        data: formDataToSend,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (response.data.success) {
        setSubmitStatus("success");
        setFormData({
          first_name: "",
          last_name: "",
          email_address: "",
          contact_number: "",
          company_name: "",
          role: "",
          message: "",
        });
      } else {
        setSubmitStatus("error");
        setErrorMessage(
          response.data.message || "Failed to send message. Please try again.",
        );
      }
    } catch (error) {
      setSubmitStatus("error");
      setErrorMessage(
        error.response?.data?.message ||
          "An error occurred. Please try again or contact us directly at connect@velozent.com",
      );
      console.error("Contact form error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: "Location",
      content: "5270 California Ave #200, Irvine, CA, 92679",
    },
    {
      icon: Phone,
      title: "Phone",
      content: (
        <a
          href="tel:+19096393504"
          className="transition-colors hover:text-burnt-sienna-500"
        >
          +1 909 639 3504
        </a>
      ),
    },
    {
      icon: Mail,
      title: "Email",
      content: (
        <a
          href="mailto:connect@velozent.com"
          className="transition-colors hover:text-burnt-sienna-500"
        >
          connect@velozent.com
        </a>
      ),
    },
  ];

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>Contact Us | Velozent - Get In Touch</title>
        <meta
          name="description"
          content="Contact Velozent to elevate your business with end-to-end healthcare technology services."
        />
        <meta
          name="keywords"
          content="contact velozent, healthcare IT contact, healthcare technology services, velozent contact information"
        />
        <meta name="robots" content="index, follow" />
        <meta
          property="og:title"
          content="Contact Us | Velozent - Get In Touch"
        />
        <meta
          property="og:description"
          content="Contact Velozent to elevate your business with end-to-end healthcare technology services."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://velozent.com/contact" />
        <meta
          property="og:image"
          content="https://velozent.com/assets/images/logo.svg"
        />
        <meta property="og:locale" content="en_US" />
        <meta name="twitter:card" content="summary" />
        <meta
          name="twitter:title"
          content="Contact Us | Velozent - Get In Touch"
        />
        <meta
          name="twitter:description"
          content="Contact Velozent to elevate your business with end-to-end healthcare technology services."
        />
        <link rel="canonical" href="https://velozent.com/contact" />
      </Helmet>
      <StructuredData type="LocalBusiness" />

      <section className="relative overflow-hidden bg-shark-950 px-4 pb-16 pt-20 sm:px-6 lg:px-8 lg:pb-20 lg:pt-28">
        <div className="hero-mesh">
          <div className="hero-mesh-orb hero-mesh-orb--1" />
          <div className="hero-mesh-orb hero-mesh-orb--2" />
          <div className="hero-mesh-orb hero-mesh-orb--3" />
          <div className="hero-mesh-grain" />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <motion.div variants={stagger} initial="hidden" animate="visible">
            <motion.p
              variants={fade}
              className="text-xs font-semibold uppercase tracking-[0.2em] text-burnt-sienna-400"
            >
              Contact Us
            </motion.p>
            <motion.h1
              variants={fade}
              className="mt-4 text-balance text-[clamp(2rem,4.6vw,3.15rem)] font-extrabold leading-[1.08] tracking-tight text-white"
            >
              We Want to{" "}
              <span className="bg-gradient-to-r from-burnt-sienna-300 via-burnt-sienna-400 to-burnt-sienna-500 bg-clip-text text-transparent">
                Hear from You
              </span>
            </motion.h1>
            <motion.p
              variants={fade}
              className="mx-auto mt-5 max-w-xl text-[1.02rem] leading-relaxed text-shark-300 lg:text-[1.08rem]"
            >
              Elevate your business with end-to-end healthcare technology
              services.
            </motion.p>
          </motion.div>
        </div>
        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-burnt-sienna-500/50 to-transparent" />
      </section>

      <motion.section
        className="bg-white px-4 py-16 sm:px-6 lg:px-8 lg:py-24"
        variants={stagger}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-12">
            <motion.div variants={fade} className="space-y-6">
              {contactInfo.map((info, index) => {
                const Icon = info.icon;
                return (
                  <div key={index} className="flex items-start gap-4">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-shark-900 text-white">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="font-semibold text-shark-900">
                        {info.title}
                      </p>
                      <p className="mt-1 text-sm text-shark-500">
                        {info.content}
                      </p>
                      {info.subContent && (
                        <p className="text-sm text-shark-500">
                          {info.subContent}
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </motion.div>

            <motion.div variants={fade} className="lg:col-span-2">
              <form onSubmit={sendMail} className="space-y-6">
                {submitStatus === "success" && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-center gap-3 rounded-lg border border-burnt-sienna-200 bg-burnt-sienna-50 p-4"
                  >
                    <CheckCircle2 className="h-5 w-5 flex-shrink-0 text-burnt-sienna-600" />
                    <p className="text-sm text-shark-700">
                      Thank you for contacting us! We'll get back to you soon.
                    </p>
                  </motion.div>
                )}

                {submitStatus === "error" && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex items-start gap-3 rounded-lg border border-burnt-sienna-800/30 bg-burnt-sienna-950/5 p-4"
                  >
                    <XCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-burnt-sienna-700" />
                    <p className="text-sm text-shark-700">{errorMessage}</p>
                  </motion.div>
                )}

                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      type="text"
                      placeholder="First Name"
                      value={formData.first_name}
                      onChange={handleChange}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="last_name">Last Name *</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      type="text"
                      placeholder="Last Name"
                      value={formData.last_name}
                      onChange={handleChange}
                      required
                      className="mt-2"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                  <div>
                    <Label htmlFor="email_address">Email *</Label>
                    <Input
                      id="email_address"
                      name="email_address"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email_address}
                      onChange={handleChange}
                      required
                      className="mt-2"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact_number">Contact Number *</Label>
                    <Input
                      id="contact_number"
                      name="contact_number"
                      type="tel"
                      placeholder="+1 (555) 123-4567"
                      value={formData.contact_number}
                      onChange={handleChange}
                      required
                      className="mt-2"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="company_name">Company Name *</Label>
                  <Input
                    id="company_name"
                    name="company_name"
                    type="text"
                    placeholder="Company Name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="role">What is your role? *</Label>
                  <Select
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    required
                    className="mt-2"
                  >
                    <option value="">- Select -</option>
                    <option value="CFO">CFO</option>
                    <option value="CEO">CEO</option>
                    <option value="CIO">CIO</option>
                    <option value="IT Director">IT Director</option>
                    <option value="Applications Director">
                      Applications Director
                    </option>
                    <option value="Health Information Manager (HIM)">
                      Health Information Manager (HIM)
                    </option>
                    <option value="Other">Other</option>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    rows={5}
                    placeholder="Please share any other details about how we can help."
                    value={formData.message}
                    onChange={handleChange}
                    required
                    className="mt-2"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  size="lg"
                  className="h-12 w-full px-8 text-base font-semibold sm:w-auto"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      Send Message
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </div>
        </div>
      </motion.section>
    </>
  );
}
